import React from "react";
import { Button, Flex } from "@jsq/ds/core";
import type { TaskStepDetail } from "../../types";

interface DefaultActionBarProps {
  task: TaskStepDetail;
  actionLoading: boolean;
  allSubtasksCompleted: boolean;
  /**
   * True when the task is unassigned OR assigned to someone other than the
   * logged-in user. Disables every action button — the BE would reject the
   * mutation and the UX shouldn't invite the click. For DATA_COLLECTION the
   * subtask-completion guard usually catches this transitively (subtasks are
   * read-only for non-assignees in TaskDetail), but this is the explicit
   * belt-and-suspenders guard for tasks with no required subtasks.
   */
  isNotAssignee?: boolean;
  isDocumentUploading?: boolean;
  onActionClick: (action: string) => void;
}

// Actions never shown in DefaultActionBar.
// skip  — admin/exception operation; data-collection users should not see it.
const HIDDEN_ACTIONS = new Set(["skip"]);

// Primary actions that should be gated on all subtasks being completed.
const SUBTASKS_GATED_ACTIONS = new Set(["complete", "mark_complete"]);

// Maps BE action.variant to a DS Button variant; defaults to "neutral".
const VARIANT_MAP: Record<string, "primary" | "neutral" | "danger"> = {
  primary: "primary",
  danger: "danger",
};

export const DefaultActionBar: React.FC<DefaultActionBarProps> = ({
  task,
  actionLoading,
  allSubtasksCompleted,
  isNotAssignee = false,
  isDocumentUploading = false,
  onActionClick,
}) => {
  const visibleActions = task.availableActions.filter((a) => !HIDDEN_ACTIONS.has(a.action));

  if (visibleActions.length === 0) return null;

  return (
    <Flex gap="8px" alignItems="center">
      {visibleActions.map((action) => {
        const isSubtasksGated = SUBTASKS_GATED_ACTIONS.has(action.action);
        const isActionDisabled =
          actionLoading ||
          (isSubtasksGated && (!allSubtasksCompleted || isDocumentUploading)) ||
          isNotAssignee;
        return (
          <Button
            key={action.action}
            variant={VARIANT_MAP[action.variant] ?? "neutral"}
            disabled={isActionDisabled}
            onClick={() => onActionClick(action.action)}
          >
            {action.label}
          </Button>
        );
      })}
    </Flex>
  );
};

export default DefaultActionBar;
